import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from '../../utils/toast'
import { Check, X } from '@phosphor-icons/react'
import { GoogleCalendarPreferences } from '../../types/User'
import {
  useLazyGetGoogleCalendarPreferencesQuery,
  useUpdateGoogleCalendarPreferencesMutation,
} from '../../redux/timeoff.endpoints'
import googleCalendarLogo from '../../assets/google_calendar_logo.png'

const GOOGLE_CALENDAR_LOGO = googleCalendarLogo

export const DEFAULT_DECLINE_MESSAGE = 'Declined because I am out of office'

const DEFAULT_PREFERENCES: GoogleCalendarPreferences = {
  syncEnabled: true,
  autoDecline: false,
  declineScope: 'new',
  declineMessage: DEFAULT_DECLINE_MESSAGE,
}

interface GoogleCalendarSettingsModalProps {
  show: boolean
  onClose: () => void
  idToken: string
}

export const GoogleCalendarSettingsModal = ({
  show,
  onClose,
  idToken,
}: GoogleCalendarSettingsModalProps) => {
  const [preferences, setPreferences] =
    useState<GoogleCalendarPreferences>(DEFAULT_PREFERENCES)

  const [fetchPreferences, { isFetching }] =
    useLazyGetGoogleCalendarPreferencesQuery()
  const [savePreferences, { isLoading: isSaving }] =
    useUpdateGoogleCalendarPreferencesMutation()

  useEffect(() => {
    if (!show || !idToken) return
    fetchPreferences(idToken)
      .unwrap()
      .then(setPreferences)
      .catch(() => {
        toast.error('Unable to load Google Calendar settings.', {
          toastId: 'gcal-settings-load-error',
        })
      })
  }, [show, idToken, fetchPreferences])

  const subEnabled = preferences.syncEnabled && preferences.autoDecline

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const handleSave = async () => {
    try {
      const saved = await savePreferences({
        token: idToken,
        preferences: {
          ...preferences,
          declineMessage: preferences.declineMessage.trim() || DEFAULT_DECLINE_MESSAGE,
        },
      }).unwrap()
      setPreferences(saved)
      toast.success('Google Calendar settings updated successfully', {
        toastId: 'gcal-settings-save-success',
      })
      onClose()
    } catch {
      toast.error('Unable to update Google Calendar settings. Please try again.', {
        toastId: 'gcal-settings-save-error',
      })
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="gcal-settings-modal-dialog"
      contentClassName="gcal-settings-modal-content"
    >
      <div className="gcal-settings-modal">
        <div className="gcal-settings-modal__header">
          <div className="gcal-settings-modal__header-left">
            <img
              src={GOOGLE_CALENDAR_LOGO}
              alt="Google Calendar"
              className="gcal-settings-modal__logo"
            />
            <h2 className="gcal-settings-modal__title">
              Google Calendar Settings
            </h2>
          </div>
          <button
            type="button"
            className="gcal-settings-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} weight="regular" />
          </button>
        </div>

        <div className="gcal-settings-modal__body">
          <div className="gcal-settings-modal__toggle-row">
            <button
              type="button"
              role="switch"
              aria-checked={preferences.syncEnabled}
              className={`gcal-settings-modal__switch${
                preferences.syncEnabled
                  ? ' gcal-settings-modal__switch--on'
                  : ''
              }`}
              onClick={() =>
                setPreferences(prev => ({
                  ...prev,
                  syncEnabled: !prev.syncEnabled,
                }))
              }
            >
              <span className="gcal-settings-modal__switch-thumb" />
            </button>
            <div>
              <p className="gcal-settings-modal__toggle-title">
                Sync time off to Google Calendar
              </p>
              <p className="gcal-settings-modal__toggle-desc">
                Automatically reflect your out-of-office status on your
                personal calendar.
              </p>
            </div>
          </div>

          <div className="gcal-settings-modal__divider" />

          <div
            className={`gcal-settings-modal__section${
              preferences.syncEnabled
                ? ''
                : ' gcal-settings-modal__section--disabled'
            }`}
          >
            <p className="gcal-settings-modal__section-label">
              Auto-Decline Configuration
            </p>

            <label
              className="gcal-settings-modal__checkbox-row"
              onClick={() =>
                preferences.syncEnabled &&
                setPreferences(prev => ({
                  ...prev,
                  autoDecline: !prev.autoDecline,
                }))
              }
            >
              <span
                role="checkbox"
                aria-checked={preferences.autoDecline}
                className={`gcal-settings-modal__checkbox${
                  preferences.autoDecline
                    ? ' gcal-settings-modal__checkbox--checked'
                    : ''
                }`}
              >
                {preferences.autoDecline && (
                  <Check size={11} weight="bold" color="#fff" />
                )}
              </span>
              Automatically decline meetings
            </label>

            <div
              className={`gcal-settings-modal__sub-options${
                subEnabled ? '' : ' gcal-settings-modal__sub-options--disabled'
              }`}
            >
              {(['new', 'all'] as const).map(value => (
                <label
                  key={value}
                  className="gcal-settings-modal__radio-row"
                  onClick={() =>
                    subEnabled &&
                    setPreferences(prev => ({ ...prev, declineScope: value }))
                  }
                >
                  <span
                    className={`gcal-settings-modal__radio${
                      preferences.declineScope === value
                        ? ' gcal-settings-modal__radio--checked'
                        : ''
                    }`}
                  >
                    {preferences.declineScope === value && (
                      <span className="gcal-settings-modal__radio-dot" />
                    )}
                  </span>
                  {value === 'new'
                    ? 'Only new meeting invitations'
                    : 'New and existing meetings'}
                </label>
              ))}

              <div className="gcal-settings-modal__message-field">
                <p className="gcal-settings-modal__message-label">
                  Decline message
                </p>
                <textarea
                  className="gcal-settings-modal__message-input"
                  value={preferences.declineMessage}
                  onChange={e =>
                    setPreferences(prev => ({
                      ...prev,
                      declineMessage: e.target.value,
                    }))
                  }
                  placeholder="Enter a custom decline message..."
                  rows={3}
                  disabled={!subEnabled}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="gcal-settings-modal__footer">
          <button
            type="button"
            className="gcal-settings-modal__btn gcal-settings-modal__btn--cancel"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="gcal-settings-modal__btn gcal-settings-modal__btn--save"
            onClick={handleSave}
            disabled={isSaving || isFetching}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default GoogleCalendarSettingsModal
